import type { AppNotification } from '@/hooks/useNotifications';

export type NotificationCategory = 'events' | 'tasks' | 'messages' | 'organizations';
export type NotificationFilter = 'all' | NotificationCategory;

export const NOTIFICATION_FILTERS: { key: NotificationFilter; label: string; emptyText: string }[] = [
  { key: 'all', label: 'All', emptyText: 'No notifications yet' },
  { key: 'events', label: 'Events', emptyText: 'No event notifications yet' },
  { key: 'tasks', label: 'Tasks', emptyText: 'No task notifications yet' },
  { key: 'messages', label: 'Messages', emptyText: 'No message notifications yet' },
  { key: 'organizations', label: 'Organizations', emptyText: 'No organization notifications yet' },
];

const CATEGORY_BY_TYPE: Record<string, NotificationCategory> = {
  volunteer_event_accepted: 'events',
  event_application_pending: 'events',
  volunteer_left_event: 'events',
  volunteer_left_shift: 'events',
  volunteer_changed_shift: 'events',
  volunteer_joined_shift: 'events',
  task_assigned: 'tasks',
  task_denied: 'tasks',
  task_submission_pending: 'tasks',
  hours_request_pending: 'tasks',
  chat_mention: 'messages',
  direct_message: 'messages',
  event_message: 'messages',
  department_message: 'messages',
  organization_membership_accepted: 'organizations',
  organization_membership_pending: 'organizations',
  organization_promoted_admin: 'organizations',
};

/** Which filter chip a type belongs to. Unknown types are only listed under "All". */
export function notificationCategory(type: string): NotificationCategory | null {
  return CATEGORY_BY_TYPE[type] ?? null;
}

export function filterNotifications(list: AppNotification[], filter: NotificationFilter): AppNotification[] {
  if (filter === 'all') return list;
  return list.filter((n) => notificationCategory(n.type) === filter);
}

export type NotificationGroupKey = 'new' | 'today' | 'yesterday' | 'last7' | 'earlier';

export type NotificationGroup = {
  key: NotificationGroupKey;
  label: string;
  items: AppNotification[];
};

const GROUP_ORDER: { key: NotificationGroupKey; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'earlier', label: 'Earlier' },
];

/** Whole local calendar days from `date` to `now`; built from y/m/d so DST shifts don't skew it. */
function calendarDaysAgo(date: Date, now: Date): number {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - day.getTime()) / 86_400_000);
}

function groupKey(n: AppNotification, now: Date): NotificationGroupKey {
  // Unread here means unread when the page loaded: markAllAsRead deliberately leaves
  // local state alone, so "New" is what arrived since the last visit.
  if (!n.read) return 'new';
  const days = calendarDaysAgo(new Date(n.updated_at ?? n.created_at), now);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'last7';
  return 'earlier';
}

export function groupNotifications(list: AppNotification[], now: Date = new Date()): NotificationGroup[] {
  const buckets = new Map<NotificationGroupKey, AppNotification[]>();
  for (const n of list) {
    const key = groupKey(n, now);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(n);
    else buckets.set(key, [n]);
  }
  return GROUP_ORDER.filter(({ key }) => buckets.has(key)).map(({ key, label }) => ({
    key,
    label,
    items: buckets.get(key) ?? [],
  }));
}
